import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { ArrowRight } from "lucide-react";

const INDUSTRIES = [
  "Professional Services", "Healthcare", "Legal", "Finance & Fintech",
  "SaaS / B2B Tech", "Consulting", "Real Estate", "Construction",
  "Hospitality", "Education", "Logistics", "Marketing & Agencies", "Other",
];

export default function AuditIntake() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [form, setForm] = useState({
    company_name: "",
    website: "",
    industry: "",
    region: "",
    competitors: ["", "", "", "", ""],
    services: "",
    context: "",
    homepage_headline: "",
    about_paragraph: "",
    top_services: ["", "", ""],
    primary_buyer: "",
    audit_kind: "snapshot",
    mode: "auto",
  });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [categoryModal, setCategoryModal] = useState(null); // {industry, suggested_category, reasoning, editValue}

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const updateCompetitor = (i) => (e) => {
    const next = [...form.competitors];
    next[i] = e.target.value;
    setForm({ ...form, competitors: next });
  };
  const updateTopService = (i) => (e) => {
    const next = [...(form.top_services || ["", "", ""])];
    next[i] = e.target.value;
    setForm({ ...form, top_services: next });
  };
  const isFullAudit = form.audit_kind === "full";
  const competitorCount = isFullAudit ? 5 : 3;

  // Actually POST the audit (called after the category is confirmed)
  const _submitAudit = async (approvedCategory) => {
    setError("");
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        competitors: form.competitors.filter(Boolean),
        top_services: (form.top_services || []).filter(Boolean),
        category_phrase: approvedCategory,
      };
      if (!isAdmin) payload.mode = "auto";
      const { data } = await api.post("/audits", payload);
      if (isAdmin && form.mode === "manual") {
        navigate(`/audit/${data.id}/manual`);
      } else if (data.status === "pending_review") {
        navigate(`/my-snapshot?pending=${data.id}`);
      } else {
        navigate(`/processing/${data.id}`);
      }
    } catch (err) {
      setError(formatApiError(err));
      setSubmitting(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    // FIX 1 — Discovery Category Gate.
    // Suggest a buyer-facing search category from the raw industry, then
    // require the operator to confirm or edit before testing begins.
    //
    // Smart-skip (iteration_7 feedback): when the operator already typed a
    // specific 2-6-word category that closely matches the LLM suggestion,
    // skip the confirm modal — the operator's intent is unambiguous and the
    // ~3s round-trip + extra click adds friction with no analytical value.
    const industry = (form.industry || "").trim();
    if (!industry) {
      setError("Industry is required.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/audits/suggest-category", {
        industry,
        company_name: form.company_name,
        website: form.website,
        services: (form.top_services || []).filter(Boolean).join(", "),
      });
      const suggested = (data?.suggested_category || industry).trim();

      // ── Smart-skip heuristic ──────────────────────────────────────────
      const norm = (s) =>
        s.toLowerCase()
          .replace(/[^a-z0-9\s]/g, " ")
          .split(/\s+/)
          .filter(Boolean);
      const STOP = new Set(["a", "an", "the", "and", "or", "of", "for", "in", "on", "to"]);
      const indTokens = norm(industry).filter((t) => !STOP.has(t));
      const sugTokens = norm(suggested).filter((t) => !STOP.has(t));
      const wc = indTokens.length;
      const isSpecific = wc >= 2 && wc <= 6;
      const overlap = sugTokens.length
        ? sugTokens.filter((t) => indTokens.includes(t)).length / sugTokens.length
        : 0;
      const isExactMatch = industry.toLowerCase() === suggested.toLowerCase();
      if (isExactMatch || (isSpecific && overlap >= 0.6)) {
        // Skip the modal entirely — submit immediately with the operator's
        // own industry phrase as the discovery category.
        return _submitAudit(industry);
      }
      setCategoryModal({
        industry,
        suggested,
        reasoning: data?.reasoning || "",
        editValue: suggested,
      });
    } catch (err) {
      // Graceful fallback — still gate on confirm, just use industry verbatim.
      setCategoryModal({
        industry,
        suggested: industry,
        reasoning: "",
        editValue: industry,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const approveCategory = () => {
    const final = (categoryModal.editValue || categoryModal.suggested || form.industry).trim();
    setForm((f) => ({ ...f, category_phrase: final }));
    setCategoryModal(null);
    _submitAudit(final);
  };

  return (
    <div className="max-w-3xl mx-auto px-6 sm:px-8 py-16" data-testid="audit-intake-page">
      <Helmet>
        <title>Run an AI Visibility Snapshot · The Entity Agency</title>
        <meta name="description" content="Submit your business details to receive your AI Visibility Score™ across ChatGPT, Claude, Gemini, and Perplexity. Snapshot delivered instantly." />
        <meta name="robots" content="noindex,follow" />
      </Helmet>
      <p className="overline">New Snapshot</p>
      <h1 className="mt-3 font-serif-display text-5xl sm:text-6xl text-[#f4efe6] leading-[1.02]" style={{fontWeight: 700}}>
        Tell us about your business.
      </h1>
      <p className="mt-5 text-[#c9bca4] leading-relaxed max-w-2xl">
        {"We'll run the canonical prompt suite across ChatGPT, Claude, Gemini, and Perplexity, and synthesize a structured AI Visibility deliverable."}
      </p>

      <form onSubmit={submit} className="mt-12 space-y-6">
        {/* Tier toggle — Snapshot (free first / $5 repeat) vs Full Audit ($300) */}
        <div data-testid="intake-tier-toggle">
          <label className="overline-soft">Audit type</label>
          <div className="mt-2 grid sm:grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => setForm({ ...form, audit_kind: "snapshot" })}
              data-testid="intake-tier-snapshot"
              className="text-left p-4 rounded-sm border transition-colors"
              style={{
                borderColor: !isFullAudit ? "#b8963e" : "#2a2620",
                background: !isFullAudit ? "rgba(184, 150, 62, 0.06)" : "transparent",
              }}
            >
              <div className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full"
                      style={{ background: !isFullAudit ? "#b8963e" : "#5e5448" }} />
                <span className="font-mono-eav text-xs tracking-wider uppercase font-bold"
                      style={{ color: !isFullAudit ? "#b8963e" : "#c9bca4" }}>
                  AI Visibility Snapshot
                </span>
              </div>
              <p className="mt-2 text-sm text-[#c9bca4]">
                A 2-page diagnostic with score, tier, recommendation test, gaps, and quick wins.
              </p>
              <p className="mt-1 font-mono-eav text-[10px] tracking-wider text-[#5e5448]">
                FIRST · FREE · ~2 MINS · 20 LLM CALLS
              </p>
            </button>
            <button
              type="button"
              onClick={() => setForm({ ...form, audit_kind: "full" })}
              data-testid="intake-tier-full"
              className="text-left p-4 rounded-sm border transition-colors"
              style={{
                borderColor: isFullAudit ? "#b8963e" : "#2a2620",
                background: isFullAudit ? "rgba(184, 150, 62, 0.06)" : "transparent",
              }}
            >
              <div className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full"
                      style={{ background: isFullAudit ? "#b8963e" : "#5e5448" }} />
                <span className="font-mono-eav text-xs tracking-wider uppercase font-bold"
                      style={{ color: isFullAudit ? "#b8963e" : "#c9bca4" }}>
                  Full AI Visibility Audit™
                </span>
                <span className="ml-auto font-mono-eav text-[10px] tracking-wider"
                      style={{ color: isFullAudit ? "#b8963e" : "#5e5448" }}>
                  $300
                </span>
              </div>
              <p className="mt-2 text-sm text-[#c9bca4]">
                10-page comprehensive deliverable: 48 data points, score projections (30/60/90),
                priority matrix, 90-day roadmap, per-platform deep dive.
              </p>
              <p className="mt-1 font-mono-eav text-[10px] tracking-wider text-[#5e5448]">
                12 PROMPTS × 4 PLATFORMS · 48 LLM CALLS · ~3-5 MINS
              </p>
            </button>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="overline-soft">Company name</label>
            <input required data-testid="intake-company-input"
                   value={form.company_name} onChange={update("company_name")}
                   placeholder="Acme Industries" className="mt-2 eav-input" />
          </div>
          <div>
            <label className="overline-soft">Website URL</label>
            <input required data-testid="intake-website-input"
                   value={form.website} onChange={update("website")}
                   placeholder="https://acme.com" className="mt-2 eav-input" />
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="intake-industry" className="text-[11px] font-mono-eav tracking-[0.2em] uppercase font-bold" style={{ color: "#b8963e" }}>
              Industry <span style={{ color: "#c46a5a" }}>*</span>
            </label>
            <input id="intake-industry" type="text" required
                   data-testid="intake-industry-input"
                   value={form.industry} onChange={update("industry")}
                   placeholder='e.g. "Commercial General Contractor", "AI Visibility agency"'
                   className="mt-2 eav-input" />
            <p className="mt-1 text-[11px] text-[#8a7e6d] leading-snug">
              Type the most specific phrase a prospect would search for to find this company.
            </p>
          </div>
          <div>
            <label className="text-[11px] font-mono-eav tracking-[0.2em] uppercase font-bold" style={{ color: "#b8963e" }}>
              Service region <span style={{ color: "#c46a5a" }}>*</span>
            </label>
            <input required data-testid="intake-region-input"
                   value={form.region} onChange={update("region")}
                   placeholder="United States, EU, NYC…"
                   className="mt-2 eav-input" />
          </div>
        </div>

        <div>
          <label className="overline-soft">
            {isFullAudit ? "Top 5 competitors (required)" : "Top 3 competitors"}
          </label>
          <div className={`mt-2 grid ${isFullAudit ? "sm:grid-cols-5" : "sm:grid-cols-3"} gap-3`}>
            {Array.from({ length: competitorCount }).map((_, i) => (
              <input key={i}
                     data-testid={`intake-competitor-${i}-input`}
                     value={form.competitors[i] || ""}
                     onChange={updateCompetitor(i)}
                     placeholder={`Competitor ${i + 1}`}
                     className="eav-input" />
            ))}
          </div>
        </div>

        {isFullAudit && (
          <>
            <div data-testid="intake-full-audit-fields" className="pt-2 space-y-4">
              <div>
                <label className="overline-soft">Top 3 services you sell (required)</label>
                <div className="mt-2 grid sm:grid-cols-3 gap-3">
                  {[0, 1, 2].map((i) => (
                    <input key={i}
                           data-testid={`intake-service-${i}-input`}
                           value={(form.top_services || [])[i] || ""}
                           onChange={updateTopService(i)}
                           placeholder={`Service ${i + 1}`}
                           className="eav-input" />
                  ))}
                </div>
                <p className="mt-1 text-[11px] text-[#8a7e6d] leading-snug">
                  The exact names a buyer would search for. Used in buyer-intent prompts.
                </p>
              </div>
              <div>
                <label className="overline-soft">Ideal buyer description (required)</label>
                <textarea rows={2}
                          data-testid="intake-buyer-input"
                          value={form.primary_buyer || ""}
                          onChange={update("primary_buyer")}
                          placeholder='e.g. "VP of Facilities at a regional hospital network planning a $20M+ build"'
                          className="mt-2 eav-input" />
                <p className="mt-1 text-[11px] text-[#8a7e6d] leading-snug">
                  {`Used in the recommendation queries (we'll ask "I'm a [buyer] and need to hire…").`}
                </p>
              </div>
            </div>
          </>
        )}

        <div>
          <label className="overline-soft">Primary services (optional)</label>
          <textarea rows={2} data-testid="intake-services-input"
                    value={form.services} onChange={update("services")}
                    placeholder="e.g. Tax strategy, M&A advisory, audit, valuation"
                    className="mt-2 eav-input" />
        </div>

        <div>
          <label className="overline-soft">Additional context (optional)</label>
          <textarea rows={3} data-testid="intake-context-input"
                    value={form.context} onChange={update("context")}
                    placeholder="Anything else the analysis should know"
                    className="mt-2 eav-input" />
        </div>

        {/* Ground-truth fields used to score Accuracy & Consistency vs. what AI says */}
        <div className="pt-4 mt-4 border-t border-[#2a2620] space-y-4">
          <div>
            <p className="font-mono-eav text-[10px] tracking-[0.22em] uppercase font-bold text-[#b8963e]">
              Ground truth from your site
            </p>
            <p className="mt-1 text-xs text-[#8a7e6d]">
              {"Paste these "}<strong className="text-[#c9bca4]">exactly</strong>{" as they appear on your homepage. We use them to score how closely AI's understanding matches reality."}
            </p>
          </div>

          <div>
            <label htmlFor="intake-headline" className="overline-soft">Homepage headline (paste exact)</label>
            <input id="intake-headline" type="text" data-testid="intake-headline-input"
                   value={form.homepage_headline} onChange={update("homepage_headline")}
                   placeholder="The hero headline shown at the top of your homepage"
                   className="mt-2 eav-input" />
          </div>

          <div>
            <label htmlFor="intake-about" className="overline-soft">About paragraph (paste exact)</label>
            <textarea id="intake-about" rows={4} data-testid="intake-about-input"
                      value={form.about_paragraph} onChange={update("about_paragraph")}
                      placeholder="Copy your About/Who-We-Are paragraph verbatim from your homepage or About page"
                      className="mt-2 eav-input" />
          </div>
        </div>

        {error && (
          <div data-testid="intake-error" className="text-sm text-[#c46a5a] bg-[#c46a5a]/10 border border-[#c46a5a]/30 rounded-md px-3 py-2">
            {error}
          </div>
        )}

        {/* Mode toggle — admin only. Customers always use auto mode. */}
        {isAdmin && (
        <div data-testid="intake-mode-toggle">
          <label className="overline-soft">Run mode</label>
          <div className="mt-2 grid sm:grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => setForm({ ...form, mode: "auto" })}
              data-testid="intake-mode-auto"
              className="text-left p-4 rounded-sm border transition-colors"
              style={{
                borderColor: form.mode === "auto" ? "#b8963e" : "#2a2620",
                background: form.mode === "auto" ? "rgba(184, 150, 62, 0.06)" : "transparent",
              }}
            >
              <div className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full"
                      style={{ background: form.mode === "auto" ? "#b8963e" : "#5e5448" }} />
                <span className="font-mono-eav text-xs tracking-wider uppercase font-bold"
                      style={{ color: form.mode === "auto" ? "#b8963e" : "#c9bca4" }}>
                  Automatic
                </span>
              </div>
              <p className="mt-2 text-sm text-[#c9bca4]">
                We run all 5 prompts on ChatGPT, Gemini, Claude &amp; Perplexity and score the snapshot automatically.
              </p>
              <p className="mt-1 font-mono-eav text-[10px] tracking-wider text-[#5e5448]">
                ~90–120 SECONDS · 20 LLM CALLS
              </p>
            </button>
            <button
              type="button"
              onClick={() => setForm({ ...form, mode: "manual" })}
              data-testid="intake-mode-manual"
              className="text-left p-4 rounded-sm border transition-colors"
              style={{
                borderColor: form.mode === "manual" ? "#b8963e" : "#2a2620",
                background: form.mode === "manual" ? "rgba(184, 150, 62, 0.06)" : "transparent",
              }}
            >
              <div className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full"
                      style={{ background: form.mode === "manual" ? "#b8963e" : "#5e5448" }} />
                <span className="font-mono-eav text-xs tracking-wider uppercase font-bold"
                      style={{ color: form.mode === "manual" ? "#b8963e" : "#c9bca4" }}>
                  Manual — paste your own outputs
                </span>
              </div>
              <p className="mt-2 text-sm text-[#c9bca4]">
                Copy our prompts, run them in ChatGPT / Gemini / Claude / Perplexity, paste outputs back &amp; score yourself.
              </p>
              <p className="mt-1 font-mono-eav text-[10px] tracking-wider text-[#5e5448]">
                INCLUDES PERPLEXITY · ZERO API COST
              </p>
            </button>
          </div>
        </div>
        )}

        <div className="pt-2 flex items-center justify-between flex-wrap gap-3">
          <p className="font-mono-eav text-xs tracking-wider text-[#5e5448]">
            {isAdmin && form.mode === "manual"
              ? "MANUAL · 4 PLATFORMS · COPY → PASTE → SCORE"
              : isFullAudit
                ? "FULL AUDIT · 48 LLM CALLS · PAY $300 → AUTO-RUN"
                : "AUTO · 4 MODELS · 5 PROMPTS · LIVE ANALYSIS"}
          </p>
          <button type="submit" disabled={submitting}
                  data-testid="intake-submit-button"
                  className="btn-gold">
            {submitting
              ? "Submitting…"
              : isAdmin && form.mode === "manual"
                ? "Continue to Manual Run"
                : isFullAudit
                  ? "Continue to Payment — $300"
                  : "Generate Snapshot"}
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </form>

      {/* Category confirmation modal */}
      {categoryModal && (
        <div
          role="dialog"
          aria-modal="true"
          data-testid="category-confirm-modal"
          className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-8 overflow-y-auto"
          style={{ background: "rgba(8, 8, 7, 0.85)" }}
          onClick={() => setCategoryModal(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-xl rounded-md"
            style={{ background: "#0f0e0c", border: "1px solid #b8963e" }}
          >
            <div style={{ background: "#b8963e", height: 6, borderTopLeftRadius: 4, borderTopRightRadius: 4 }} />
            <div className="p-6 sm:p-8">
              <p className="text-[11px] font-mono-eav tracking-[0.22em] uppercase font-bold" style={{ color: "#b8963e" }}>
                Confirm Search Category
              </p>
              <h2 className="mt-2 font-serif-display text-3xl text-[#f4efe6]" style={{ fontWeight: 600 }}>
                What will AI be asked to search for?
              </h2>
              <p className="mt-3 text-sm text-[#c9bca4] leading-relaxed">
                This is the exact phrase a prospect would type into ChatGPT, Gemini, Claude, or Perplexity to find this company.
                The right phrase makes or breaks the AI Discovery Presence score.
              </p>

              <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <div className="rounded-sm p-3" style={{ background: "#1a1815" }}>
                  <p className="text-[10px] font-mono-eav tracking-[0.2em] uppercase" style={{ color: "#5e5448" }}>
                    Industry entered
                  </p>
                  <p className="mt-1 text-[#c9bca4]">{categoryModal.industry}</p>
                </div>
                <div className="rounded-sm p-3" style={{ background: "#1a1815", border: "1px solid #b8963e" }}>
                  <p className="text-[10px] font-mono-eav tracking-[0.2em] uppercase" style={{ color: "#b8963e" }}>
                    Suggested category
                  </p>
                  <p className="mt-1 text-[#f4efe6] font-medium">{`"${categoryModal.suggested}"`}</p>
                </div>
              </div>

              {categoryModal.reasoning && (
                <p className="mt-4 text-xs italic font-serif-display text-[#8a7e6d] leading-relaxed">
                  {categoryModal.reasoning}
                </p>
              )}

              <div className="mt-6">
                <label className="text-[10px] font-mono-eav tracking-[0.2em] uppercase" style={{ color: "#8a7e6d" }}>
                  Approve or edit
                </label>
                <input
                  type="text"
                  data-testid="category-confirm-input"
                  value={categoryModal.editValue}
                  onChange={(e) => setCategoryModal({ ...categoryModal, editValue: e.target.value })}
                  className="mt-2 eav-input"
                />
              </div>

              <div className="mt-6 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setCategoryModal(null)}
                  data-testid="category-confirm-cancel"
                  className="btn-ghost text-sm"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={approveCategory}
                  disabled={!categoryModal.editValue.trim() || submitting}
                  data-testid="category-confirm-approve"
                  className="btn-gold text-sm"
                >
                  {submitting ? "Submitting…" : "Approve & Run Audit"}
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
